/**
 * Case matters for react-select-async-paginate — uses `/list-case-matters-v2` (page + limit, has_next).
 */

import { useQuery } from '@tanstack/react-query';
import { listCaseMattersV2 } from 'services/CaseServices';
import { queryKeys } from '../config/queryClient';

const normalizePracticeAreaIdItem = (item) => {
  if (item == null || item === '') return '';
  if (typeof item === 'object') {
    const id = item.practice_area_id ?? item.value ?? item.id;
    return id != null && id !== '' ? String(id) : '';
  }
  return String(item);
};

export const toPracticeAreaParam = (practiceAreaId) => {
  if (practiceAreaId === undefined || practiceAreaId === null || practiceAreaId === '') return undefined;
  if (Array.isArray(practiceAreaId)) {
    const joined = practiceAreaId.map(normalizePracticeAreaIdItem).filter(Boolean).join(',');
    return joined || undefined;
  }
  if (typeof practiceAreaId === 'object') {
    const id = normalizePracticeAreaIdItem(practiceAreaId);
    return id || undefined;
  }
  return String(practiceAreaId);
};

const mapRowToOption = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = row.case_matter_id ?? row.sk ?? row.pk;
  return {
    label: row.matter_name,
    value: id,
    ...row,
  };
};

const extractRowsFromV2Response = (response) => {
  const raw = response?.data;
  const list = raw?.data ?? raw?.['case-matter'] ?? [];
  return { raw, rows: Array.isArray(list) ? list : [] };
};

/**
 * Case matters list via v2. Default: one request with `full_list` (server returns full set). When `maxPages === 1`, fetches a single page without `full_list` (e.g. capped preview).
 *
 * @param {Object} opts
 * @param {boolean} [opts.excludeFromLoading=false]
 * @param {string|Array} [opts.practiceAreaId='']
 * @param {boolean} [opts.is_pi_case=false]
 * @param {number} [opts.pageSize=20] — used only when `maxPages === 1` (v2 `limit`); also sent as `limit` with `full_list`
 * @param {number} [opts.maxPages] — pass `1` for a single non–full_list page; omit for `full_list`
 * @returns {Promise<Array>}
 */
export const fetchAllCaseMattersV2 = async ({
  excludeFromLoading = false,
  practiceAreaId = '',
  is_pi_case = false,
  pageSize = 20,
  maxPages,
} = {}) => {
  const practice_area_id = toPracticeAreaParam(practiceAreaId);

  if (maxPages === 1) {
    const response = await listCaseMattersV2({
      limit: pageSize,
      page: 1,
      sort_by: 'matter_name',
      sort_order: 'asc',
      excludeFromLoading,
      practice_area_id,
      is_pi_case,
    });
    const { rows } = extractRowsFromV2Response(response);
    return Array.isArray(rows) ? rows : [];
  }

  const response = await listCaseMattersV2({
    limit: pageSize,
    page: 1,
    sort_by: 'matter_name',
    sort_order: 'asc',
    excludeFromLoading,
    practice_area_id,
    is_pi_case,
    full_list: true,
  });
  const { rows } = extractRowsFromV2Response(response);
  return Array.isArray(rows) ? rows : [];
};

/**
 * Fetch one page of case matters for AsyncPaginate or label hydration.
 * @param {Object} options
 * @param {number} [options.pageSize=20]
 * @param {number} [options.page=1]
 * @param {string} [options.searchQuery='']
 * @param {string|Array} [options.practiceAreaId='']
 * @param {boolean} [options.is_pi_case=false]
 */
export const fetchCaseMattersPage = async ({ pageSize = 20, page = 1, searchQuery = '', practiceAreaId = '', is_pi_case = false }) => {
  const response = await listCaseMattersV2({
    limit: pageSize,
    page,
    sort_by: 'matter_name',
    sort_order: 'asc',
    excludeFromLoading: true,
    practice_area_id: toPracticeAreaParam(practiceAreaId),
    keyword: searchQuery?.trim() ? searchQuery.trim() : undefined,
    is_pi_case,
  });

  const { raw, rows } = extractRowsFromV2Response(response);

  let options = rows.map(mapRowToOption).filter(Boolean);

  // Client-side filter when API ignores keyword (narrow current page only).
  // Keep rows whose id exactly matches the query (e.g. UUID hydrate) even if label does not include it.
  if (searchQuery?.trim() && options.length) {
    const qRaw = searchQuery.trim();
    const q = qRaw.toLowerCase();
    options = options.filter((o) => String(o?.value ?? o?.case_matter_id ?? '') === qRaw || o.label?.toLowerCase().includes(q));
  }

  /** Drive AsyncPaginate infinite scroll — library only loads more when user scrolls near bottom. */
  const hasNext = Boolean(raw?.has_next);
  const hasPrevious = Boolean(raw?.has_previous);
  const currentPage = Number(raw?.page) > 0 ? Number(raw.page) : page;

  return {
    options,
    hasMore: hasNext,
    additional: {
      page: currentPage + 1,
      hasNext,
      hasPrevious,
    },
  };
};

/**
 * @param {Object} params
 * @param {string} [params.searchQuery]
 * @param {number} [params.page]
 * @param {number} [params.pageSize]
 * @param {string|Array} [params.practiceAreaId]
 * @param {boolean} [params.is_pi_case]
 * @param {boolean} [params.enabled]
 */
export const useCaseMattersPaginated = ({
  searchQuery = '',
  page = 1,
  pageSize = 20,
  practiceAreaId = '',
  is_pi_case = false,
  enabled = true,
} = {}) => {
  const queryKey = queryKeys.caseMatters.paginated({
    searchQuery,
    page,
    pageSize,
    practiceAreaId,
    is_pi_case,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchCaseMattersPage({
        pageSize,
        page,
        searchQuery,
        practiceAreaId,
        is_pi_case,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    data: data || { options: [], hasMore: false, additional: { page: 1, hasNext: false, hasPrevious: false } },
    isLoading,
    error,
    refetch,
  };
};

/**
 * Load options for AsyncPaginate (CaseMatterSelect) — v2 list, page in `additional.page`.
 */
export const loadCaseMatterOptions = async (search, loadedOptions, additional, config = {}) => {
  const { pageSize = 20, practiceAreaId = '', is_pi_case = false } = config;
  const page = typeof additional?.page === 'number' && additional.page >= 1 ? additional.page : 1;

  try {
    return await fetchCaseMattersPage({
      pageSize,
      page,
      searchQuery: search,
      practiceAreaId,
      is_pi_case,
    });
  } catch (error) {
    console.error('Error loading case matter options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { page: 1, hasNext: false, hasPrevious: false },
    };
  }
};
